import React, { useState, useEffect, useRef } from 'react';
import {
  Row,
  Col,
  Input,
  Button,
  Card,
  Typography,
  Space,
  Spin,
  Alert,
  Table,
  Grid,
  AutoComplete,
  message,
  Tooltip,
  Breadcrumb,
  Popconfirm,
  Modal,
  Tour,
} from 'antd';
import {
  SearchOutlined,
  StarOutlined,
  StarFilled,
  LoadingOutlined,
  RiseOutlined,
  InfoCircleOutlined,
  RocketOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';
import axios from 'axios';
// import { mockApi } from '../mockApi'; // モックAPIは削除済み
import { v2AnalysisApi, v2ArticlesApi } from '../../../api';
import favoriteKeywordsAPI from '../../../api/favoriteKeywords';
import { getUserProfile } from '../../../auth';
import './steps.css';
import './CompetitorAnalysis.css';
import TipsRotator from '../../Common/TipsRotator';
import { researchTips, waitingTips } from '../../Common/tipsData';
import { useTutorialTour } from '../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../config/tourConfigs';
import EnhancedTour, { TourHelpButton } from '../../Common/EnhancedTour';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const ThemeSetup = ({
  sessionData = { keyword: '' },
  updateSessionData = () => {},
  userProfile,
  loading,
  setLoading,
  onNext = () => {},
  isGuest = false,
  onGuestAction = null,
}) => {
  const screens = useBreakpoint();
  const [keyword, setKeyword] = useState(sessionData.keyword || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [favoriteKeywords, setFavoriteKeywords] = useState([]);
  const [activeAnalyses, setActiveAnalyses] = useState([]);
  const inputRef = useRef(null);
  const [existingAnalysisModalVisible, setExistingAnalysisModalVisible] =
    useState(false);
  const [existingAnalysis, setExistingAnalysis] = useState(null);

  // Tour用のref
  const keywordInputRef = useRef(null);
  const generationFlowCardRef = useRef(null);
  const favoriteCardRef = useRef(null);
  const startButtonRef = useRef(null);

  // Tour管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
  } = useTutorialTour('research_input');

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }

    fetchFavoriteKeywords();
  }, []);

  // キーワードが変更された時の処理
  useEffect(() => {
    if (keyword.trim() && keyword !== sessionData.keyword) {
      // セッションデータを更新（デバウンス）
      const updateTimer = setTimeout(() => {
        updateSessionData({
          keyword: keyword.trim(),
        });
      }, 1000);

      return () => clearTimeout(updateTimer);
    }
  }, [keyword, sessionData.keyword, updateSessionData]);

  // アクティブリサーチの10秒毎取得
  useEffect(() => {
    let mounted = true;
    const loadActive = async () => {
      try {
        const list = await v2AnalysisApi.getActiveAnalyses();
        if (mounted) setActiveAnalyses(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setActiveAnalyses([]);
      }
    };
    loadActive();
    const t = setInterval(loadActive, 10000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, []);

  // お気に入りキーワード取得
  const fetchFavoriteKeywords = async () => {
    try {
      const response = await favoriteKeywordsAPI.getFavoriteKeywords({
        limit: 50,
        includeAnalysisInfo: true,
      });

      // APIレスポンスをテーブル用のフォーマットに変換
      const formattedKeywords = response.keywords.map(kw => ({
        keyword: kw.keyword,
        volume: kw.search_volume || 0,
        difficulty: kw.keyword_difficulty
          ? kw.keyword_difficulty < 30
            ? '低'
            : kw.keyword_difficulty < 70
              ? '中'
              : '高'
          : '-',
        trend: 'stable', // トレンドデータがない場合のデフォルト
        hasExistingAnalysis: kw.has_existing_analysis,
        latestAnalysisId: kw.latest_analysis_id,
        latestAnalysisStatus: kw.latest_analysis_status,
        addedAt: kw.added_at,
        analysisCount: kw.analysis_count,
      }));

      setFavoriteKeywords(formattedKeywords);
    } catch (error) {
      console.error('Failed to fetch favorite keywords:', error);
      // エラー時は空配列を設定
      setFavoriteKeywords([]);
    }
  };

  // レコード生成を確認する関数
  const waitForAnalysisRecord = async analysisId => {
    const maxWait = 10000; // 最大10秒待機
    const interval = 500; // 500msごとにチェック
    let waited = 0;

    while (waited < maxWait) {
      try {
        const status = await v2AnalysisApi.getAnalysisStatus(analysisId);
        if (status && status.analysis_id) {
          console.log('Analysis record confirmed:', status);
          return true;
        }
      } catch (error) {
        console.log('Waiting for analysis record...', waited);
      }

      await new Promise(resolve => setTimeout(resolve, interval));
      waited += interval;
    }

    console.warn('Analysis record not confirmed within timeout');
    return false;
  };

  // オートコンプリート
  const fetchAutoComplete = debounce(async searchText => {
    if (!searchText || searchText.length < 1) {
      setAutoCompleteOptions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/seo-article/autocomplete-keywords?query=${encodeURIComponent(searchText)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      );
      setAutoCompleteOptions(
        response.data.map(keyword => ({ value: keyword })),
      );
    } catch (error) {
      console.error('Failed to fetch autocomplete keywords:', error);
      // エラー時は空配列を設定
      setAutoCompleteOptions([]);
    }
  }, 300);

  // キーワード正規化（半角/全角スペースを統一）
  const normalizeKeyword = keyword => {
    return keyword
      .trim()
      .replace(/[\s\u3000]+/g, ' ') // 全角・半角スペースを半角スペース1つに統一
      .toLowerCase();
  };

  // 既存のリサーチをチェック
  const checkExistingAnalysis = async keyword => {
    try {
      const normalizedKeyword = normalizeKeyword(keyword);

      // 自分のリサーチ一覧を取得（最新100件）
      const response = await v2AnalysisApi.listAnalyses({
        skip: 0,
        limit: 100,
        q: keyword.trim(), // キーワードでフィルタリング
      });

      console.log('📊 checkExistingAnalysis response:', response);

      // レスポンスは {analyses: [...], total_count: 123} という構造
      const analyses = response?.analyses || response?.data?.analyses || [];

      if (analyses.length > 0) {
        // 完全一致するキーワードを探す
        const matchingAnalysis = analyses.find(item => {
          const itemKeyword = normalizeKeyword(item.keyword || '');
          console.log(
            `🔍 Comparing: "${itemKeyword}" === "${normalizedKeyword}"`,
          );
          return itemKeyword === normalizedKeyword;
        });

        console.log('✅ Matching analysis found:', matchingAnalysis);
        return matchingAnalysis || null;
      }
      return null;
    } catch (error) {
      console.error('Failed to check existing analysis:', error);
      return null;
    }
  };

  // キーワードリサーチ開始（実際の実行部分）
  const executeAnalysis = async () => {
    if (!keyword.trim()) {
      message.warning('キーワードを入力してください');
      return;
    }

    // 🔧 重複実行防止：既にリサーチ中の場合は処理しない
    if (analyzing) {
      console.log(
        '🔧 [DUPLICATE_PREVENTION] Analysis already in progress, ignoring request',
      );
      return;
    }

    setAnalyzing(true);

    try {
      // キーワードを保存
      updateSessionData({
        keyword: keyword.trim(),
      });

      console.log('Starting analysis for keyword:', keyword.trim());

      // バックグラウンドリサーチを開始
      const result = await v2AnalysisApi.startAnalysis({
        keyword: keyword.trim(),
        location: 'Japan',
        language: 'ja',
        target_audience: '一般',
        content_length: '3000-5000文字',
      });

      console.log('Analysis started, result:', result);
      message.success('新規リサーチを予約しました');

      // 遷移前に完全なセッションデータを準備
      const initialAnalysisData = {
        keyword: keyword.trim(),
        step1AnalysisResult: {
          analysis_id: result.analysis_id,
          keyword: keyword.trim(),
          status: 'queued',
          progress: 0,
          current_step: 'リサーチを待機しています...',
        },
        // 空のcompetitorDataを設定してスケルトン表示を防ぐ
        competitorData: {
          analysis_id: result.analysis_id,
          keyword: keyword.trim(),
          status: 'queued',
          loading: true,
        },
      };

      updateSessionData(initialAnalysisData);

      // レコード生成を確認してから遷移
      await waitForAnalysisRecord(result.analysis_id);

      // リサーチ結果画面へ遷移
      onNext(result.analysis_id);
    } catch (error) {
      message.error('リサーチの開始に失敗しました');
      console.error('Analysis start failed:', error);
    } finally {
      // 必ずフラグを解除
      setAnalyzing(false);
    }
  };

  const analyzeKeyword = async () => {
    if (isGuest && onGuestAction) {
      onGuestAction();
      return;
    }
    if (!keyword.trim()) {
      message.warning('キーワードを入力してください');
      return;
    }

    // 🔧 重複実行防止：既にリサーチ中の場合は処理しない
    if (analyzing) {
      console.log(
        '🔧 [DUPLICATE_PREVENTION] Analysis already in progress, ignoring request',
      );
      return;
    }

    // 既存のリサーチをチェック
    const existing = await checkExistingAnalysis(keyword.trim());

    if (existing) {
      // 既存のリサーチが見つかった場合、モーダルを表示
      console.log('📊 [EXISTING_CHECK] Found existing analysis, showing modal');
      setExistingAnalysis(existing);
      setExistingAnalysisModalVisible(true);
    } else {
      // 既存のリサーチがない場合、そのまま実行
      console.log('🚀 [NEW_ANALYSIS] No existing analysis found, starting new');
      executeAnalysis();
    }
  };

  // 既存のリサーチを見に行く
  const viewExistingAnalysis = () => {
    if (existingAnalysis) {
      setExistingAnalysisModalVisible(false);
      onNext(existingAnalysis.analysis_id);
    }
  };

  // 新規リサーチを強制実行
  const forceNewAnalysis = () => {
    setExistingAnalysisModalVisible(false);
    executeAnalysis();
  };

  // お気に入りキーワードを選択
  const selectFavoriteKeyword = record => {
    setKeyword(record.keyword);
    updateSessionData({ keyword: record.keyword });
  };

  // お気に入りキーワードを削除
  const removeFavoriteKeyword = async keyword => {
    try {
      await favoriteKeywordsAPI.deleteFavoriteKeyword(keyword);
      message.success(`「${keyword}」をお気に入りから削除しました`);
      // リストを再読み込み
      fetchFavoriteKeywords();
    } catch (error) {
      console.error('お気に入りキーワードの削除に失敗:', error);
      message.error(
        `お気に入りキーワードの削除に失敗しました: ${error.message}`,
      );
    }
  };

  // テーブルカラム定義
  const columns = [
    {
      title: 'キーワード',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <Popconfirm
              title="お気に入りを外しますか？"
              okText="はい"
              cancelText="いいえ"
              onConfirm={e => {
                e?.stopPropagation?.();
                removeFavoriteKeyword(record.keyword);
              }}
              onCancel={e => e?.stopPropagation?.()}
            >
              <StarFilled
                style={{ color: '#faad14', cursor: 'pointer' }}
                onClick={e => e.stopPropagation()}
              />
            </Popconfirm>
            <Tooltip title="このキーワードでリサーチを開始">
              <a onClick={() => selectFavoriteKeyword(record)}>{text}</a>
            </Tooltip>
          </Space>
        </Space>
      ),
    },
    {
      title: 'リサーチ',
      key: 'action',
      align: 'center',
      render: (_, record) =>
        record.hasExistingAnalysis ? (
          <Button
            type="link"
            size="small"
            onClick={e => {
              e.stopPropagation();
              window.open(`/aio/analysis/${record.latestAnalysisId}`, '_blank');
            }}
          >
            結果
          </Button>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  // モバイル用のシンプルなカラム
  const mobileColumns = [
    {
      title: 'キーワード',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (text, record) => (
        <div onClick={() => selectFavoriteKeyword(record)}>
          <Space direction="vertical" size="small">
            <Space>
              <Popconfirm
                title="お気に入りを外しますか？"
                okText="はい"
                cancelText="いいえ"
                onConfirm={e => {
                  e?.stopPropagation?.();
                  removeFavoriteKeyword(record.keyword);
                }}
                onCancel={e => e?.stopPropagation?.()}
              >
                <StarFilled
                  style={{ color: '#faad14', cursor: 'pointer' }}
                  onClick={e => e.stopPropagation()}
                />
              </Popconfirm>
              <a>{text}</a>
            </Space>
          </Space>
        </div>
      ),
    },
  ];

  // Tourステップ定義
  const tourSteps = [
    {
      title: TOUR_CONFIGS.research_input.steps[0].title,
      description: TOUR_CONFIGS.research_input.steps[0].description,
      target: null,
    },
    {
      title: TOUR_CONFIGS.research_input.steps[1].title,
      description: TOUR_CONFIGS.research_input.steps[1].description,
      target: () => generationFlowCardRef.current,
      placement: 'top',
    },
    {
      title: TOUR_CONFIGS.research_input.steps[2].title,
      description: TOUR_CONFIGS.research_input.steps[2].description,
      target: () => keywordInputRef.current,
      placement: 'bottom',
    },
    {
      title: TOUR_CONFIGS.research_input.steps[3].title,
      description: TOUR_CONFIGS.research_input.steps[3].description,
      target: () => favoriteCardRef.current,
      placement: 'left',
    },
    {
      title: TOUR_CONFIGS.research_input.steps[4].title,
      description: TOUR_CONFIGS.research_input.steps[4].description,
      target: () => startButtonRef.current,
      placement: 'top',
    },
  ];

  return (
    <div className="step-container theme-setup">
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="新規リサーチ"
        />
      )}
      {/* ページヘッダー（パンくず・タイトル・説明） */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '16px 0' }}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <a href="/aio/dashboard">AIOコンテンツモード</a>
          </Breadcrumb.Item>
          <Breadcrumb.Item>新規リサーチ</Breadcrumb.Item>
        </Breadcrumb>
        <TourHelpButton
          onClick={restartTour}
          tooltip="ガイドツアーを再表示"
        />
      </div>
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={1} className="gradient-text" style={{ fontSize: 24 }}>
              新規リサーチ
            </Title>
            <Typography.Text type="secondary">
              狙いたい検索キーワードを入力して、リサーチを予約します
            </Typography.Text>
          </Col>
        </Row>
      </div>

      <Row gutter={[24, 24]}>
        {/* 左 6割 */}
        <Col xs={24} md={14}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* キーワード入力フォーム */}
            <>
              <div
                className="keyword-input-container"
                style={{ position: 'relative', height: '56px' }}
                ref={keywordInputRef}
              >
                <AutoComplete
                  ref={inputRef}
                  size="large"
                  options={autoCompleteOptions}
                  value={keyword}
                  onChange={value => setKeyword(value)}
                  onSelect={value => setKeyword(value)}
                  onSearch={fetchAutoComplete}
                  style={{ width: '100%' }}
                >
                  <Input
                    placeholder="例：SEO AEO 違い"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        if (e.ctrlKey) {
                          e.preventDefault();
                          if (!analyzing) analyzeKeyword();
                        } else {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }
                    }}
                    style={{
                      paddingRight: 180,
                      borderRadius: 28,
                      fontSize: 16,
                      height: 56,
                      paddingLeft: 32,
                      border: '2px solid #f0f0f0',
                      transition: 'all 0.3s ease',
                    }}
                    className="modern-search-input"
                  />
                </AutoComplete>
                <Tooltip
                  title="新規リサーチには一回30クレジットが必要です"
                  placement="top"
                >
                  <Button
                    ref={startButtonRef}
                    type="primary"
                    size="large"
                    loading={analyzing || loading}
                    onClick={analyzeKeyword}
                    icon={<SearchOutlined />}
                    style={{
                      position: 'absolute',
                      right: 4,
                      top: 4,
                      height: 48,
                      borderRadius: 24,
                      paddingLeft: 24,
                      paddingRight: 32,
                      fontSize: 15,
                      fontWeight: 500,
                      boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    新規リサーチスタート
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        marginLeft: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <FireOutlined style={{ color: '#fff' }} />
                      30
                    </span>
                  </Button>
                </Tooltip>
              </div>

              {/* フロー説明セクション */}
              <Card
                style={{
                  marginTop: 24,
                  background:
                    'linear-gradient(135deg, #f6f8fb 0%, #fafbfc 100%)',
                  border: '1px solid #e8eaed',
                }}
                className="generation-flow-card"
                ref={generationFlowCardRef}
                bodyStyle={{ padding: '24px 32px' }}
              >
                <Row gutter={[24, 16]} align="middle">
                  <Col xs={24} sm={11}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: '#1890ff',
                          marginBottom: 8,
                        }}
                      >
                        1. リサーチ（分析）
                      </div>
                      <div
                        style={{
                          padding: '16px 20px',
                          borderRadius: 8,
                          border: '1px solid #1890ff',
                          boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)',
                        }}
                      >
                        <Text style={{ fontSize: 14, color: '#595959' }}>
                          狙いたいキーワードで
                          <br />
                          競合サイト・検索意図を
                          <br />
                          徹底的に分析
                        </Text>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={2} style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        fontSize: 24,
                        color: '#8c8c8c',
                        fontWeight: 600,
                      }}
                    >
                      →
                    </div>
                  </Col>
                  <Col xs={24} sm={11}>
                    <div style={{ textAlign: 'center' }}>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: '#52c41a',
                          marginBottom: 8,
                        }}
                      >
                        2. 生成
                      </div>
                      <div
                        style={{
                          padding: '16px 20px',
                          borderRadius: 8,
                          border: '1px solid #52c41a',
                          boxShadow: '0 2px 8px rgba(82, 196, 26, 0.1)',
                        }}
                      >
                        <Text style={{ fontSize: 14, color: '#595959' }}>
                          リサーチ結果を活用して
                          <br />
                          AIO/SEOに最適化された
                          <br />
                          高品質コンテンツを生成
                        </Text>
                      </div>
                    </div>
                  </Col>
                </Row>
                <div
                  style={{
                    marginTop: 20,
                    paddingTop: 20,
                    borderTop: '1px solid #e8eaed',
                  }}
                >
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: '100%' }}
                  >
                    <Text
                      strong
                      style={{
                        fontSize: 14,
                        color: '#262626',
                        display: 'block',
                      }}
                    >
                      推奨フロー
                    </Text>
                    <Text
                      style={{
                        fontSize: 13,
                        color: '#595959',
                        lineHeight: 1.6,
                      }}
                    >
                      AIO/SEOで成果を出すためには、
                      <Text strong style={{ color: '#1890ff' }}>
                        深いリサーチが必須
                      </Text>
                      です。まずこの画面でキーワードリサーチを実行し、競合分析・検索意図分析を完了させてから、
                      <Text strong style={{ color: '#52c41a' }}>
                        その結果をコンテンツ生成で使用
                      </Text>
                      することを強く推奨します。
                    </Text>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 12,
                        color: '#8c8c8c',
                        display: 'block',
                      }}
                    >
                      ※リサーチなしでも生成は可能ですが、分析データを活用することで生成精度が大きく向上します。
                    </Text>
                  </Space>
                </div>
              </Card>
            </>

            {/* TIPS（リサーチ画面用） */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <TipsRotator
                tips={[...researchTips, ...waitingTips]}
                intervalMs={7000}
                fadeMs={300}
              />
            </div>

            {/* 実行状況（10秒毎更新） */}
            {activeAnalyses.length > 0 && (
              <Card
                hoverable={false}
                title={<span className="run-queue-title">実行・予約状況</span>}
                style={{
                  marginTop: 16,
                  background:
                    'linear-gradient(135deg, rgba(158, 130, 216, 0.02) 0%, rgba(217, 70, 239, 0.02) 100%)',
                }}
              >
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  {activeAnalyses.map((item, idx) => (
                    <Row
                      key={item.analysis_id}
                      align="middle"
                      gutter={8}
                      style={{
                        padding: '8px 0',
                        borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0',
                      }}
                    >
                      <Col flex="auto">
                        {item.status === 'queued' ? (
                          <Text className="search-intent-text">
                            「{item.keyword}」のリサーチを予約しています。
                          </Text>
                        ) : (
                          <Text className="search-intent-text">
                            「{item.keyword}」のリサーチを実行中...［
                            {item.progress}
                            %］
                          </Text>
                        )}
                      </Col>
                      <Col>
                        {item.status === 'queued' ? (
                          <Button
                            type="default"
                            size="small"
                            className="violet-outline-btn"
                            onClick={async () => {
                              try {
                                await v2AnalysisApi.cancelAnalysis(
                                  item.analysis_id,
                                );
                                message.success('予約をキャンセルしました');
                                const list =
                                  await v2AnalysisApi.getActiveAnalyses();
                                setActiveAnalyses(
                                  Array.isArray(list) ? list : [],
                                );
                              } catch (e) {
                                message.error('キャンセルに失敗しました');
                              }
                            }}
                          >
                            キャンセルする
                          </Button>
                        ) : (
                          <Button
                            type="default"
                            size="small"
                            className="violet-outline-btn"
                            onClick={() =>
                              window.open(
                                `/aio/analysis/${item.analysis_id}`,
                                '_blank',
                              )
                            }
                          >
                            詳細を見る
                          </Button>
                        )}
                      </Col>
                    </Row>
                  ))}
                </Space>
              </Card>
            )}
          </Space>
        </Col>
        {/* 右 4割（やや狭め/右寄せ） */}
        <Col xs={24} md={9} style={{ marginLeft: 'auto' }}>
          <Card className="v2-card" hoverable={false} ref={favoriteCardRef}>
            <Title level={5} style={{ marginBottom: 16 }}>
              お気に入りキーワード
              <Tooltip title="よく使用するキーワードの履歴">
                <InfoCircleOutlined
                  style={{ fontSize: 14, marginLeft: 8, color: '#8c8c8c' }}
                />
              </Tooltip>
            </Title>
            {favoriteKeywords.length > 0 ? (
              <Table
                columns={screens.xs ? mobileColumns : columns}
                dataSource={favoriteKeywords}
                rowKey="keyword"
                pagination={false}
                size="middle"
                onRow={record => ({
                  style: { cursor: 'pointer' },
                })}
              />
            ) : (
              <Paragraph type="secondary" style={{ margin: 0 }}>
                リサーチ結果画面から関連キーワードをお気に入り登録できます
              </Paragraph>
            )}
          </Card>
        </Col>
      </Row>

      {/* 既存リサーチ確認モーダル */}
      <Modal
        open={existingAnalysisModalVisible}
        onCancel={() => setExistingAnalysisModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: '24px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <ExclamationCircleOutlined
              style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }}
            />
            <Title level={4} style={{ marginBottom: 8 }}>
              既にリサーチ済みのキーワードです
            </Title>
            <Text type="secondary">
              同じキーワードで過去にリサーチを実行しています
            </Text>
          </div>

          {existingAnalysis && (
            <Card
              style={{
                marginBottom: 24,
                background: '#fafafa',
                border: '1px solid #e8e8e8',
              }}
            >
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    キーワード
                  </Text>
                  <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                    {existingAnalysis.keyword}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    実行日時
                  </Text>
                  <div style={{ fontSize: 14, marginTop: 4 }}>
                    {existingAnalysis.created_at
                      ? new Date(existingAnalysis.created_at).toLocaleString(
                          'ja-JP',
                          {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          },
                        )
                      : '-'}
                  </div>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ステータス
                  </Text>
                  <div style={{ marginTop: 4 }}>
                    {existingAnalysis.status === 'completed' ? (
                      <span style={{ color: '#52c41a', fontSize: 14 }}>
                        完了
                      </span>
                    ) : existingAnalysis.status === 'running' ? (
                      <span style={{ color: '#1890ff', fontSize: 14 }}>
                        実行中
                      </span>
                    ) : existingAnalysis.status === 'error' ? (
                      <span style={{ color: '#ff4d4f', fontSize: 14 }}>
                        エラー
                      </span>
                    ) : (
                      <span style={{ color: '#8c8c8c', fontSize: 14 }}>
                        {existingAnalysis.status}
                      </span>
                    )}
                  </div>
                </div>
              </Space>
            </Card>
          )}

          <Alert
            message="新たにリサーチを実行しますか？"
            description={
              <div>
                既存のリサーチ結果を確認するか、同じキーワードで新規リサーチを実行できます。
                <div
                  style={{
                    marginTop: '8px',
                    color: '#ff7a45',
                    fontWeight: 'bold',
                  }}
                >
                  ※新規リサーチを実行するとクレジットが消費されます
                </div>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Space style={{ width: '100%', justifyContent: 'center' }} size={12}>
            <Button
              size="large"
              onClick={viewExistingAnalysis}
              style={{ minWidth: 180 }}
              disabled={analyzing}
            >
              既存のリサーチを見る
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={forceNewAnalysis}
              loading={analyzing}
              disabled={analyzing}
              style={{ minWidth: 180 }}
            >
              新規リサーチを実行
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default ThemeSetup;
